import React, { useState } from "react";
import { Info, RotateCw } from "lucide-react";
import {
  Button,
  Modal,
  RichTextEditor,
  SegmentedControl,
} from "../../../components/ui";
import { sameRichText } from "../../../lib/richText";
import type {
  PolicyDocumentId,
  PolicyDocuments,
  StoredPolicyDocuments,
} from "../types/brand.types";
import PolicyPagePreview from "./PolicyPagePreview";

/**
 * What the tab says about each of the two documents: the words over the
 * editor, the placeholder in an empty one, and the name the preview and the
 * screen reader call it by.
 */
const DOCUMENTS: Array<{
  id: PolicyDocumentId;
  label: string;
  heading: string;
  placeholder: string;
}> = [
  {
    id: "guidelines",
    label: "Guidelines",
    heading: "Your guidelines",
    placeholder:
      "Write the guidelines a client should follow on any of your plans - meal timing, water, what to avoid…",
  },
  {
    id: "policy",
    label: "Policy",
    heading: "Policy editor",
    placeholder:
      "Write the terms your plans are sold under - how a plan is shared, how to reach you, what a plan's validity is…",
  },
];

interface BrandPoliciesTabProps {
  /** The two documents as they are being written - what the editor shows. */
  documents: PolicyDocuments;
  /**
   * The two as stored - what Cancel puts back and Save compares against,
   * and where the version and the client's rendering of each come from.
   */
  stored: StoredPolicyDocuments;
  onChange: (patch: Partial<PolicyDocuments>) => void;
  onSave: () => void;
  onCancel: () => void;
  /**
   * Puts the system default back on one document - on the server, at once.
   * The stored copy and the box both follow what comes back.
   */
  onReset: (id: PolicyDocumentId) => void;
  saving: boolean;
  resetting: boolean;
}

/**
 * My brand → Guidelines & policy: the two pieces of writing a plan carries
 * with it, each written on the left and read back on the right as a client
 * will see it.
 *
 * One editor and one preview, switched between the two documents by the pill
 * row at the top, rather than two of each: they are the same kind of thing
 * written in the same way, and the switch is what says which is open. The
 * Save row saves both - whichever has changed - so switching documents
 * mid-edit loses nothing, and the page's unsaved prompt covers the pair.
 *
 * The state lives on the page, the way the Details form's does, so a switch
 * to another tab and back opens on what was being written rather than on
 * the stored copy.
 */
const BrandPoliciesTab: React.FC<BrandPoliciesTabProps> = ({
  documents,
  stored,
  onChange,
  onSave,
  onCancel,
  onReset,
  saving,
  resetting,
}) => {
  const [active, setActive] = useState<PolicyDocumentId>("guidelines");
  const [confirmingReset, setConfirmingReset] = useState(false);

  const current = DOCUMENTS.find((document) => document.id === active)!;
  const html = documents[active];
  const record = stored[active];

  // Either document off its stored copy - the Save row is for both.
  const dirty = DOCUMENTS.some(
    (document) =>
      !sameRichText(documents[document.id], stored[document.id].body)
  );
  const activeDirty = !sameRichText(html, record.body);

  // A reset has something to do while the document is a published one, or
  // while the box has moved off the default it is on. On the default and
  // untouched, there is nothing to put back.
  const canReset = record.isCustom || activeDirty;

  // What the client reads. The server writes it - the policy's clause
  // tokens replaced by their sentences - so while the box matches the stored
  // copy it is the server's rendering that is shown, as given. Once the box
  // has moved off it there is no rendering yet, and the draft is shown as
  // written, tokens and all, until it is saved.
  const previewHtml = activeDirty ? html : record.resolvedBody;

  const reset = () => {
    setConfirmingReset(false);
    onReset(active);
  };

  const busy = saving || resetting;

  return (
    // From `lg` up the tab owns what is left of the viewport, the way the
    // Form tab does, so the editor and the preview each scroll inside their
    // own card rather than growing the page.
    <div className="flex flex-col lg:h-full lg:min-h-0">
      <SegmentedControl
        items={DOCUMENTS}
        value={active}
        onChange={setActive}
        label="Document"
        className="flex-shrink-0"
      />

      {/* The preview column is sized by its sheet rather than fixed, the way
          the Details tab's is by its invoice, so the editor has whatever the
          page does not need. `grid-rows-[minmax(0,1fr)]` from `lg` up, for
          the reason the Form tab gives: a row left to `auto` is sized by what
          is in it, and the two cards would grow the page instead of
          scrolling inside it. */}
      <div className="mt-4 grid grid-cols-1 gap-5 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_auto] lg:grid-rows-[minmax(0,1fr)] 3xl:gap-6">
        {/* The editor. */}
        <div className="flex flex-col lg:min-h-0">
          <div className="mb-3 flex flex-shrink-0 flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[15px] 3xl:text-[16px] text-ink-muted">
                {current.heading}
              </h2>
              {/* Which of the two this is: the text Naurist hands out, or
                  the practice's own, and how many times it has been
                  published. `is_custom: false` and `version: 0` come
                  together - there is no version 0 row. */}
              <span className="rounded-full bg-[#F1F3F5] px-2 py-0.5 text-[11px] 3xl:text-[12px] font-medium text-ink-muted">
                {record.isCustom
                  ? `Version ${record.version} · Customised`
                  : "Naurist default"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setConfirmingReset(true)}
              disabled={!canReset || busy}
              className="flex items-center gap-1.5 text-[13px] 3xl:text-[14px] text-ink-muted transition-colors hover:text-ink disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-ink-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded"
            >
              <RotateCw aria-hidden="true" className="h-4 w-4" />
              {resetting ? "Resetting…" : "Reset to default text"}
            </button>
          </div>

          <RichTextEditor
            // Keyed on the document, so a switch is a fresh box: the other
            // document's undo history and caret have nothing to do with this
            // one, and the browser's own undo stack would otherwise carry
            // across.
            key={active}
            label={current.label}
            value={html}
            onChange={(next) => onChange({ [active]: next })}
            placeholder={current.placeholder}
            className="lg:min-h-0 lg:flex-1"
            contentClassName="min-h-[360px] lg:min-h-0"
            footer={
              // Pinned under the text it saves, the way the Details form's
              // row is pinned under its sections.
              <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
                <p className="flex items-center gap-1.5 text-[12px] 3xl:text-[13px] text-ink-subtle">
                  <Info aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
                  Saved changes affect only future plans.
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={onCancel}
                    disabled={!dirty || busy}
                    className="min-w-[100px]"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="md"
                    onClick={onSave}
                    disabled={!dirty || busy}
                    className="min-w-[130px]"
                  >
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
            }
          />
        </div>

        {/* The preview: the document on A4 sheets. Drawn from the box while
            it is being written, so it moves with every keystroke, and from
            the server's rendering once the box is back on the stored copy. */}
        <div className="flex flex-col lg:min-h-0">
          <div className="mb-3 flex flex-shrink-0 items-center justify-between gap-2">
            <h2 className="text-[15px] 3xl:text-[16px] text-ink-muted">
              How the client sees it
            </h2>
            <span className="text-[12px] 3xl:text-[13px] text-ink-subtle">
              Live preview
            </span>
          </div>
          <PolicyPagePreview
            html={previewHtml}
            label={`${current.label} preview`}
            title={current.label}
          />
        </div>
      </div>

      <Modal
        open={confirmingReset}
        title="Reset to default text"
        hideTitle
        onClose={() => setConfirmingReset(false)}
        footer={
          <>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setConfirmingReset(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={reset}>
              Reset
            </Button>
          </>
        }
      >
        {/* Heading and rule drawn inset to the panel's padding, the way the
            unsaved-changes prompt draws its own. Hidden from screen readers:
            the modal's `h2` already carries the name. */}
        <h3
          aria-hidden="true"
          className="text-[20px] 3xl:text-[22px] font-medium text-ink"
        >
          Reset to default text
        </h3>
        <p className="mt-4 text-[14px] 3xl:text-[15px] leading-[1.6] text-ink-muted">
          This will replace your current {current.label.toLowerCase()} with
          the default Naurist {current.label.toLowerCase()}, right away - it
          goes on every plan created from now on. Any custom changes you’ve
          made will be removed.
        </p>
        <div className="mt-4 border-t border-line" />
      </Modal>
    </div>
  );
};

export default BrandPoliciesTab;
